// File: frontend/src/pages/StudentsPage.jsx

import React, { useState, useEffect, useMemo } from 'react';
import Spinner from '../components/Spinner';
import toast from 'react-hot-toast';
import { studentsAPI, reportsAPI, classesAPI } from '../services/api';
import Modal from '../components/Modal';
import BulkImport from '../components/Admin/BulkImport';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import EmptyState from '../components/EmptyState';

export default function StudentsPage() {
    const { currentUser } = useAuth();
    const { t, language } = useLanguage();
    const [students, setStudents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingStudent, setEditingStudent] = useState(null);
    const [cramSchoolOptions, setCramSchoolOptions] = useState([]);


    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [historyStudent, setHistoryStudent] = useState(null);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [selectedStudentIds, setSelectedStudentIds] = useState([]);

    const [categoryFilter, setCategoryFilter] = useState('all'); // 'all', 'private', 'cram_school'
    const [searchTerm, setSearchTerm] = useState('');
    const [cramSchoolFilter, setCramSchoolFilter] = useState('all');
    const [sortBy, setSortBy] = useState('name_asc'); // 'name_asc', 'name_desc'

    // Multi-pick class assignment modal states
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [availableClasses, setAvailableClasses] = useState([]);
    const [selectedClassIds, setSelectedClassIds] = useState([]);
    const [classesLoading, setClassesLoading] = useState(false);
    const [classFilter, setClassFilter] = useState('all'); // 'all', 'private', 'cram_school'
    const [classSearchTerm, setClassSearchTerm] = useState('');

    // Add Student modal state
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [addError, setAddError] = useState('');
    const [newStudentType, setNewStudentType] = useState('private');
    const [newCramSchoolName, setNewCramSchoolName] = useState('');
    const [newName, setNewName] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [newPhone, setNewPhone] = useState('');
    const [newIsTrial, setNewIsTrial] = useState(false);
    const [newBirthday, setNewBirthday] = useState('');
    const [newParentInfo, setNewParentInfo] = useState('');
    const [newParentName, setNewParentName] = useState('');
    const [newParentPhone, setNewParentPhone] = useState('');
    const [newParentLineId, setNewParentLineId] = useState('');
    const [newPaymentSchedule, setNewPaymentSchedule] = useState('per_class');
    const [newPaymentMethod, setNewPaymentMethod] = useState('cash');
    const [newCustomRate, setNewCustomRate] = useState('');
    const [newUsername, setNewUsername] = useState('');
    const [newPassword, setNewPassword] = useState('');

    const isAdmin = currentUser?.role === 'admin';

    const fetchStudents = () => {
        setIsLoading(true);
        studentsAPI.getAll()
            .then(data => {
                setStudents(data);
                setSelectedStudentIds([]);
            })
            .catch(err => console.error("Failed to fetch students:", err))
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        fetchStudents();
        studentsAPI.getCramSchools().then(setCramSchoolOptions).catch(() => {});
    }, []);

    const resetAddForm = () => {
        setNewStudentType('private'); setNewCramSchoolName(''); setNewName(''); setNewEmail('');
        setNewPhone(''); setNewIsTrial(false); setNewBirthday(''); setNewParentInfo('');
        setNewParentName(''); setNewParentPhone(''); setNewParentLineId('');
        setNewPaymentSchedule('per_class'); setNewPaymentMethod('cash'); setNewCustomRate('');
        setNewUsername(''); setNewPassword(''); setAddError('');
    };

    const handleOpenAddModal = () => {
        resetAddForm();
        setIsAddModalOpen(true);
        studentsAPI.getCramSchools().then(setCramSchoolOptions).catch(() => {});
    };

    const handleAddStudent = async (e) => {
        e.preventDefault();
        setAddError('');
        const studentData = {
            name: newName,
            email: newEmail ? newEmail.trim() : null,
            phone: newPhone || null,
            is_trial: newIsTrial,
            birthday: newBirthday || null,
            parent_info: newStudentType === 'cram_school' ? null : (newParentInfo || null),
            parent_name: newStudentType === 'cram_school' ? null : (newParentName || null),
            parent_phone: newStudentType === 'cram_school' ? null : (newParentPhone || null),
            parent_line_id: newStudentType === 'cram_school' ? null : (newParentLineId || null),
            payment_schedule: newStudentType === 'cram_school' ? 'per_class' : newPaymentSchedule,
            payment_method: newStudentType === 'cram_school' ? 'cash' : newPaymentMethod,
            custom_rate: newStudentType === 'cram_school' ? null : (newCustomRate ? parseFloat(newCustomRate) : null),
            username: newStudentType === 'cram_school' ? null : (newUsername || null),
            password: newStudentType === 'cram_school' ? null : (newPassword || null),
            student_type: newStudentType,
            cram_school_name: newStudentType === 'cram_school' ? newCramSchoolName : null
        };
        const addPromise = studentsAPI.create(studentData);
        toast.promise(addPromise, {
            loading: 'Adding student...',
            success: (newStudent) => {
                setStudents(prev => [...prev, newStudent]);
                setIsAddModalOpen(false);
                resetAddForm();
                return <b>Student added!</b>;
            },
            error: (err) => <b>{err.response?.data?.error || 'Could not add student.'}</b>
        });
    };

    // Filter students by category
    const privateCount = students.filter(s => (s.student_type || 'private') === 'private').length;
    const cramSchoolCount = students.filter(s => s.student_type === 'cram_school').length;

    const filteredStudents = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        const result = students.filter(student => {
            const type = student.student_type || 'private';
            if (categoryFilter !== 'all' && type !== categoryFilter) return false;
            if (cramSchoolFilter !== 'all' && (student.cram_school_name || '') !== cramSchoolFilter) return false;
            if (!term) return true;
            return (
                (student.name && student.name.toLowerCase().includes(term)) ||
                (student.cram_school_name && student.cram_school_name.toLowerCase().includes(term)) ||
                (student.email && student.email.toLowerCase().includes(term)) ||
                (student.parent_name && student.parent_name.toLowerCase().includes(term))
            );
        });
        result.sort((a, b) => sortBy === 'name_desc'
            ? (b.name || '').localeCompare(a.name || '')
            : (a.name || '').localeCompare(b.name || ''));
        return result;
    }, [students, categoryFilter, cramSchoolFilter, searchTerm, sortBy]);

    // Toggle single student selection
    const handleToggleSelectStudent = (studentId) => {
        setSelectedStudentIds(prev =>
            prev.includes(studentId)
                ? prev.filter(id => id !== studentId)
                : [...prev, studentId]
        );
    };

    // Toggle select all visible students
    const handleToggleSelectAll = () => {
        const displayedIds = filteredStudents.map(s => s.id);
        const allDisplayedSelected = displayedIds.length > 0 && displayedIds.every(id => selectedStudentIds.includes(id));
        if (allDisplayedSelected) {
            setSelectedStudentIds(prev => prev.filter(id => !displayedIds.includes(id)));
        } else {
            setSelectedStudentIds(prev => Array.from(new Set([...prev, ...displayedIds])));
        }
    };

    // Bulk delete selected students
    const handleBulkDelete = async () => {
        if (selectedStudentIds.length === 0) return;
        const count = selectedStudentIds.length;
        if (window.confirm(`Are you sure you want to permanently delete ${count} selected student${count > 1 ? 's' : ''}? This will also delete their attendance records and enrollments.`)) {
            const deletePromise = studentsAPI.bulkDelete(selectedStudentIds);
            toast.promise(deletePromise, {
                loading: `Deleting ${count} student${count > 1 ? 's' : ''}...`,
                success: () => {
                    setStudents(prev => prev.filter(s => !selectedStudentIds.includes(s.id)));
                    setSelectedStudentIds([]);
                    return <b>Successfully deleted {count} student{count > 1 ? 's' : ''}!</b>;
                },
                error: (err) => <b>{err.response?.data?.error || 'Could not delete selected students.'}</b>
            });
        }
    };

    // Open multi-pick class assignment modal
    const handleOpenAssignModal = async () => {
        setIsAssignModalOpen(true);
        setSelectedClassIds([]);
        setClassSearchTerm('');
        setClassesLoading(true);
        try {
            const cls = await classesAPI.getAll();
            setAvailableClasses(cls);
        } catch (err) {
            toast.error('Failed to load classes.');
        } finally {
            setClassesLoading(false);
        }
    };

    const handleToggleClass = (classId) => {
        setSelectedClassIds(prev => 
            prev.includes(classId) ? prev.filter(id => id !== classId) : [...prev, classId]
        );
    };

    const filteredAvailableClasses = useMemo(() => {
        return availableClasses.filter(c => {
            const matchesCategory = classFilter === 'all' || (c.class_type || 'private') === classFilter;
            const term = classSearchTerm.trim().toLowerCase();
            const matchesSearch = !term ||
                (c.name && c.name.toLowerCase().includes(term)) ||
                (c.teacher_name && c.teacher_name.toLowerCase().includes(term)) ||
                (c.cram_school_name && c.cram_school_name.toLowerCase().includes(term)) ||
                (c.day && c.day.toLowerCase().includes(term));
            return matchesCategory && matchesSearch;
        });
    }, [availableClasses, classFilter, classSearchTerm]);

    const handleSelectAllClasses = () => {
        const filteredClassIds = filteredAvailableClasses.map(c => c.id);
        const allSelected = filteredClassIds.length > 0 && filteredClassIds.every(id => selectedClassIds.includes(id));
        if (allSelected) {
            setSelectedClassIds(prev => prev.filter(id => !filteredClassIds.includes(id)));
        } else {
            setSelectedClassIds(prev => Array.from(new Set([...prev, ...filteredClassIds])));
        }
    };

    const handleConfirmAssignClasses = async () => {
        if (selectedClassIds.length === 0 || selectedStudentIds.length === 0) {
            toast.error('Please select at least one class and one student.');
            return;
        }
        const loadingToast = toast.loading(`Enrolling ${selectedStudentIds.length} student(s) into ${selectedClassIds.length} class(es)...`);
        try {
            const res = await classesAPI.batchEnroll(selectedClassIds, selectedStudentIds);
            toast.success(res.message || `Successfully enrolled students!`, { id: loadingToast });
            setIsAssignModalOpen(false);
            setSelectedClassIds([]);
            setSelectedStudentIds([]);
            fetchStudents();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Could not enroll students in classes.', { id: loadingToast });
        }
    };

    // Export all students to CSV with UTF-8 BOM for Excel compatibility
    const handleExportCSV = () => {
        if (!students || students.length === 0) {
            toast.error('No students to export.');
            return;
        }

        const headers = [
            'ID', 'Name', 'Category', 'Cram School', 'Email', 'Phone', 'Birthday', 'Is Trial',
            'Family Name', 'Parent Name', 'Parent Phone', 'Parent LINE ID',
            'Billing Schedule', 'Payment Method', 'Custom Rate (NTD)', 'Created At'
        ];

        const rows = students.map(s => [
            s.id,
            `"${(s.name || '').replace(/"/g, '""')}"`,
            s.student_type === 'cram_school' ? 'Cram School' : 'Private Tutoring',
            `"${(s.cram_school_name || '').replace(/"/g, '""')}"`,
            `"${(s.email || '').replace(/"/g, '""')}"`,
            `"${(s.phone || '').replace(/"/g, '""')}"`,
            s.birthday ? s.birthday.split('T')[0] : '',
            s.is_trial ? 'Yes' : 'No',
            `"${(s.family_name || '').replace(/"/g, '""')}"`,
            `"${(s.parent_name || '').replace(/"/g, '""')}"`,
            `"${(s.parent_phone || '').replace(/"/g, '""')}"`,
            `"${(s.parent_line_id || '').replace(/"/g, '""')}"`,
            s.payment_schedule || 'per_class',
            s.payment_method || 'cash',
            s.custom_rate || '',
            s.created_at ? s.created_at.split('T')[0] : ''
        ]);

        const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `students_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success(`Exported ${students.length} students to CSV!`);
    };

    // Function to open the modal and pre-fill it with student data
    const handleOpenEditModal = (student) => {
        const formattedStudent = {
            ...student,
            student_type: student.student_type || 'private',
            cram_school_name: student.cram_school_name || '',
            birthday: student.birthday ? student.birthday.split('T')[0] : '',
            parent_name: student.parent_name || '',
            parent_phone: student.parent_phone || '',
            parent_line_id: student.parent_line_id || '',
            payment_schedule: student.payment_schedule || 'per_class',
            payment_method: student.payment_method || 'cash',
            custom_rate: student.custom_rate || '',
            password: '' // Always start with a blank password field for reset
        };
        setEditingStudent(formattedStudent);
        setIsEditModalOpen(true);
        studentsAPI.getCramSchools().then(setCramSchoolOptions).catch(() => {});
    };
    
    // NEW: Function to handle form input changes in the edit modal
    const handleEditFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditingStudent(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // NEW: Function to submit the update to the backend
    const handleUpdateStudent = async (e) => {
        e.preventDefault();
        const payload = {
            ...editingStudent,
            email: editingStudent.email ? editingStudent.email.trim() : null
        };
        const updatePromise = studentsAPI.update(payload.id, payload);
        toast.promise(updatePromise, {
            loading: 'Saving student...',
            success: (updatedStudent) => {
                setStudents(prev => prev.map(s => s.id === updatedStudent.id ? updatedStudent : s));
                setIsEditModalOpen(false);
                return <b>Student saved successfully!</b>;
            },
            error: (err) => <b>{err.response?.data?.error || 'Could not save student.'}</b>
        });
    };

    const handleDeleteStudent = async (studentId) => {
        if (window.confirm('Are you sure you want to permanently delete this student?')) {
            // Use a toast promise for great UX
            toast.promise(
                studentsAPI.delete(studentId),
                {
                    loading: 'Deleting student...',
                    success: () => {
                        // On success, update the UI and return a success message
                        setStudents(prev => prev.filter(s => s.id !== studentId));
                        setSelectedStudentIds(prev => prev.filter(id => id !== studentId));
                        return <b>Student deleted successfully!</b>;
                    },
                    error: (err) => <b>{err.response?.data?.error || 'Could not delete student.'}</b>,
                }
            );
        }
    };

    const handleOpenHistoryModal = async (student) => {
        setHistoryStudent(student);
        setHistoryModalOpen(true);
        setHistoryLoading(true);
        try {
            // We can reuse the reports API logic by filtering for this student
            // This requires a new backend endpoint
            const data = await reportsAPI.getStudentHistory(student.id); // NEW API CALL
            setHistoryData(data);
        } catch (err) {
            toast.error("Could not load student history.");
        } finally {
            setHistoryLoading(false);
        }
    };

    if (isLoading) {
        return <div className="p-10"><Spinner /></div>;
    }

    return (
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        <span>👥</span> Student Management
                    </h2>
                    {isAdmin && selectedStudentIds.length > 0 && (
                        <p className="text-xs sm:text-sm text-blue-600 font-medium mt-0.5">
                            {selectedStudentIds.length} of {students.length} student{selectedStudentIds.length > 1 ? 's' : ''} selected
                        </p>
                    )}
                </div>
                {isAdmin && (
                    <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                        <button
                            onClick={handleOpenAddModal}
                            className="flex-1 sm:flex-initial inline-flex items-center justify-center px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg shadow-xs transition-colors cursor-pointer"
                        >
                            + Add Student
                        </button>
                        <button
                            onClick={handleExportCSV}
                            className="flex-1 sm:flex-initial inline-flex items-center justify-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg shadow-xs transition-colors cursor-pointer"
                            title="Export all students to CSV file"
                        >
                            <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Export CSV
                        </button>
                        {selectedStudentIds.length > 0 && (
                            <>
                                <button
                                    onClick={handleOpenAssignModal}
                                    className="flex-1 sm:flex-initial inline-flex items-center justify-center px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow-xs transition-colors cursor-pointer"
                                    title="Assign selected students to one or more classes"
                                >
                                    <span className="mr-1.5 text-sm leading-none">🏫</span>
                                    Assign to Class(es) ({selectedStudentIds.length})
                                </button>
                                <button
                                    onClick={handleBulkDelete}
                                    className="flex-1 sm:flex-initial inline-flex items-center justify-center px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-lg shadow-xs transition-colors cursor-pointer"
                                >
                                    <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    Delete ({selectedStudentIds.length})
                                </button>
                            </>
                        )}
                        <div className="w-full sm:w-auto">
                            <BulkImport onImportSuccess={fetchStudents} />
                        </div>
                    </div>
                )}
            </div>

            {/* Category Filter Tabs */}
            <div className="flex overflow-x-auto no-scrollbar items-center gap-2 mb-4 border-b pb-3 -mx-4 px-4 sm:mx-0 sm:px-0">
                <button
                    onClick={() => setCategoryFilter('all')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                        categoryFilter === 'all'
                            ? 'bg-gray-900 text-white shadow-xs'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                    }`}
                >
                    {t('stu_all')} ({students.length})
                </button>
                <button
                    onClick={() => setCategoryFilter('private')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                        categoryFilter === 'private'
                            ? 'bg-blue-600 text-white shadow-xs'
                            : 'bg-blue-50 text-blue-800 hover:bg-blue-100'
                    }`}
                >
                    <span>🏠</span>
                    <span>{t('stu_private')} ({privateCount})</span>
                </button>
                <button
                    onClick={() => setCategoryFilter('cram_school')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                        categoryFilter === 'cram_school'
                            ? 'bg-amber-600 text-white shadow-xs'
                            : 'bg-amber-50 text-amber-900 hover:bg-amber-100'
                    }`}
                >
                    <span>🏫</span>
                    <span>{t('stu_cram')} ({cramSchoolCount})</span>
                </button>
            </div>

            {/* Search & Cram School Filters */}
            <div className="flex flex-wrap gap-2 mb-4">
                <input
                    type="text"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Search by name, cram school, email, or parent..."
                    className="flex-1 min-w-[200px] border rounded-lg p-2 text-sm"
                />
                {categoryFilter !== 'private' && cramSchoolOptions.length > 0 && (
                    <select
                        value={cramSchoolFilter}
                        onChange={e => setCramSchoolFilter(e.target.value)}
                        className="border rounded-lg p-2 text-sm bg-white dark:bg-gray-800"
                    >
                        <option value="all">All Cram Schools</option>
                        {cramSchoolOptions.map(name => <option key={name} value={name}>{name}</option>)}
                    </select>
                )}
                <select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                    className="border rounded-lg p-2 text-sm bg-white dark:bg-gray-800"
                >
                    <option value="name_asc">Name (A → Z)</option>
                    <option value="name_desc">Name (Z → A)</option>
                </select>
            </div>
            {(searchTerm || cramSchoolFilter !== 'all') && (
                <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2 mb-3">
                    Showing {filteredStudents.length} of {students.length} students
                </p>
            )}

            {/* Select All Checkbox bar on Mobile */}
            {isAdmin && filteredStudents.length > 0 && (
                <div className="flex md:hidden items-center justify-between bg-gray-50 dark:bg-gray-800/60 p-2.5 rounded-lg border mb-3 text-xs">
                    <label className="flex items-center gap-2 cursor-pointer font-medium text-gray-700 dark:text-gray-300">
                        <input
                            type="checkbox"
                            className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 h-4 w-4 cursor-pointer"
                            checked={filteredStudents.length > 0 && filteredStudents.every(s => selectedStudentIds.includes(s.id))}
                            onChange={handleToggleSelectAll}
                        />
                        <span>Select All Displayed ({filteredStudents.length})</span>
                    </label>
                </div>
            )}

            {/* --- MOBILE CARDS VIEW (< 768px) --- */}
            <div className="block md:hidden space-y-3">
                {filteredStudents.length > 0 ? (
                    filteredStudents.map(student => (
                        <div 
                            key={`mobile-student-${student.id}`} 
                            className={`p-4 rounded-xl border transition-colors shadow-xs space-y-2.5 ${selectedStudentIds.includes(student.id) ? 'bg-blue-50/90 border-blue-300' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex items-start gap-2.5">
                                    {isAdmin && (
                                        <input
                                            type="checkbox"
                                            className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 h-4 w-4 mt-1 cursor-pointer"
                                            checked={selectedStudentIds.includes(student.id)}
                                            onChange={() => handleToggleSelectStudent(student.id)}
                                        />
                                    )}
                                    <div>
                                        <button 
                                            onClick={() => handleOpenHistoryModal(student)} 
                                            className="text-base font-bold text-blue-600 hover:underline text-left cursor-pointer"
                                        >
                                            {student.name}
                                        </button>
                                        <div className="flex flex-wrap gap-1.5 mt-1">
                                            {student.student_type === 'cram_school' ? (
                                                <span className="inline-flex items-center text-[11px] font-bold text-amber-900 bg-amber-100 px-2 py-0.5 rounded-full">
                                                    🏫 {student.cram_school_name || 'Cram School'}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center text-[11px] text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full font-medium">
                                                    🏠 Private
                                                </span>
                                            )}
                                            {student.is_trial && (
                                                <span className="bg-amber-100 text-amber-800 text-[11px] px-2 py-0.5 rounded-full font-bold">Trial</span>
                                            )}
                                            {student.family_name && student.student_type !== 'cram_school' && (
                                                <span className="inline-flex items-center text-[11px] text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded">
                                                    👨‍👩‍👧 {student.family_name}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button 
                                        onClick={() => handleOpenEditModal(student)} 
                                        className="text-xs font-semibold text-blue-600 hover:text-blue-800 p-1.5 bg-blue-50 rounded-md"
                                    >
                                        Edit
                                    </button>
                                    <button 
                                        onClick={() => handleDeleteStudent(student.id)} 
                                        className="text-xs font-semibold text-red-600 hover:text-red-800 p-1.5 bg-red-50 rounded-md"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>

                            {/* Rate & Billing Info */}
                            <div className="bg-gray-50 dark:bg-gray-800/60 p-2.5 rounded-lg border border-gray-100 dark:border-gray-700 text-xs">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-500 dark:text-gray-400 font-medium">Billing / Rate:</span>
                                    {student.student_type === 'cram_school' ? (
                                        <span className="font-bold text-purple-900">
                                            {student.custom_rate ? `$${student.custom_rate} NTD/hr` : 'Per Cram Contract'}
                                        </span>
                                    ) : (
                                        <span className="font-bold text-gray-900 dark:text-gray-100">
                                            {student.custom_rate ? `$${student.custom_rate} NTD` : 'Tutoring Tier'}
                                        </span>
                                    )}
                                </div>
                                <div className="flex justify-between items-center mt-1 text-[11px] text-gray-600 dark:text-gray-400">
                                    <span>Payment Method:</span>
                                    <span className="font-medium">
                                        {student.student_type === 'cram_school' 
                                            ? '🏦 Cram School Settlement'
                                            : (student.payment_method === 'cash' ? '💵 Cash' : (student.payment_method === 'line_pay' ? '🟢 LINE Pay' : '🏦 Bank Transfer'))
                                        }
                                    </span>
                                </div>
                            </div>

                            {/* Contact Info */}
                            <div className="text-xs text-gray-600 dark:text-gray-400 flex flex-wrap justify-between items-center gap-1 pt-1 border-t border-gray-100 dark:border-gray-700">
                                {student.student_type === 'cram_school' ? (
                                    <span className="text-gray-500 dark:text-gray-400">{student.parent_info || 'Cram school managed'}</span>
                                ) : (
                                    <>
                                        <span>Parent: {student.parent_name || 'N/A'}</span>
                                        {student.parent_phone && (
                                            <a href={`tel:${student.parent_phone}`} className="text-blue-600 hover:underline">
                                                📞 {student.parent_phone}
                                            </a>
                                        )}
                                        {student.parent_line_id && (
                                            <span className="text-green-600 font-mono">LINE: {student.parent_line_id}</span>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    <EmptyState message="No students found." />
                )}
            </div>

            {/* --- DESKTOP TABLE VIEW (>= 768px) --- */}
            <div className="hidden md:block overflow-x-auto border rounded-xl shadow-xs">
                <table className="min-w-full bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800/60">
                        <tr>
                            {isAdmin && (
                                <th className="w-12 py-3 px-4 text-center">
                                    <input
                                        type="checkbox"
                                        className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 h-4 w-4 cursor-pointer"
                                        checked={filteredStudents.length > 0 && filteredStudents.every(s => selectedStudentIds.includes(s.id))}
                                        onChange={handleToggleSelectAll}
                                        title="Select all displayed students"
                                    />
                                </th>
                            )}
                            <th className="py-3 px-4 text-left font-semibold text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wider">Student Name</th>
                            <th className="py-3 px-4 text-left font-semibold text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wider">Billing Plan & Rate</th>
                            <th className="py-3 px-4 text-left font-semibold text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wider">Parent / Contact</th>
                            <th className="py-3 px-4 text-left font-semibold text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wider">Birthday</th>
                            <th className="py-3 px-4 text-left font-semibold text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wider">Trial</th>
                            <th className="py-3 px-4 text-left font-semibold text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredStudents.length > 0 ? (
                            filteredStudents.map(student => (
                                <tr 
                                    key={student.id} 
                                    className={`hover:bg-gray-50 border-b transition-colors ${selectedStudentIds.includes(student.id) ? 'bg-blue-50' : ''}`}
                                >
                                    {isAdmin && (
                                        <td className="py-2 px-4 text-center">
                                            <input
                                                type="checkbox"
                                                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 h-4 w-4 cursor-pointer"
                                                checked={selectedStudentIds.includes(student.id)}
                                                onChange={() => handleToggleSelectStudent(student.id)}
                                            />
                                        </td>
                                    )}
                                    <td className="py-3 px-4">
                                        <div className="flex flex-col">
                                            <button onClick={() => handleOpenHistoryModal(student)} className="text-blue-600 hover:underline font-bold text-left">{student.name}</button>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {student.student_type === 'cram_school' ? (
                                                    <span className="inline-flex items-center text-[11px] font-bold text-amber-900 bg-amber-100 px-2 py-0.5 rounded-full">
                                                        🏫 {student.cram_school_name || 'Cram School'}
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center text-[11px] text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded font-medium">
                                                        🏠 Private
                                                    </span>
                                                )}
                                                {student.family_name && student.student_type !== 'cram_school' && (
                                                    <span className="inline-flex items-center text-[11px] text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded">
                                                        👨‍👩‍👧 {student.family_name}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="py-3 px-4">
                                        {student.student_type === 'cram_school' ? (
                                            <div className="flex flex-col gap-1 text-xs">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded font-bold w-max bg-purple-100 text-purple-800">
                                                    🏫 {student.cram_school_name || 'Cram School'} Rate
                                                </span>
                                                <span className="text-gray-800 dark:text-gray-200 font-semibold">
                                                    {student.custom_rate ? `$${student.custom_rate} NTD/hr` : 'Per Cram Contract'}
                                                </span>
                                                <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                                    {student.payment_method === 'cash' ? t('pay_cash') : (language === 'zh' ? '🏦 銀行轉帳 (月結)' : '🏦 Bank Transfer (Monthly)')}
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col gap-1 text-xs">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded font-medium w-max ${student.payment_method === 'cash' ? 'bg-amber-100 text-amber-800' : (student.payment_method === 'line_pay' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800')}`}>
                                                    {student.payment_method === 'cash' ? t('pay_cash') : (student.payment_method === 'line_pay' ? t('pay_line_pay') : t('pay_bank'))}
                                                </span>
                                                <span className="text-gray-500 dark:text-gray-400">
                                                    {student.payment_schedule === 'monthly'
                                                        ? (language === 'zh' ? '月結帳單' : 'Monthly Bill')
                                                        : student.payment_schedule === 'per_4_sessions'
                                                            ? (language === 'zh' ? '每 4 堂結算' : 'Every 4 Sessions')
                                                            : (language === 'zh' ? '單堂結算' : 'Per-Class (Weekly)')}
                                                    {student.custom_rate ? ` • $${student.custom_rate} NTD` : ''}
                                                </span>
                                            </div>
                                        )}
                                    </td>
                                    <td className="py-3 px-4 text-sm">
                                        {student.student_type === 'cram_school' ? (
                                            <div>
                                                {student.parent_info ? (
                                                    <span className="text-xs text-gray-600 dark:text-gray-400">{student.parent_info}</span>
                                                ) : (
                                                    <span className="text-xs text-gray-400 dark:text-gray-500 italic">No direct parent contact</span>
                                                )}
                                            </div>
                                        ) : (
                                            <div>
                                                <div className="font-medium text-gray-900 dark:text-gray-100">{student.parent_name || student.email}</div>
                                                {student.parent_phone && <div className="text-xs text-gray-500 dark:text-gray-400">{student.parent_phone}</div>}
                                                {student.parent_line_id && <div className="text-xs text-green-600 font-mono font-medium">LINE: {student.parent_line_id}</div>}
                                            </div>
                                        )}
                                    </td>
                                    <td className="py-3 px-4 text-sm">{student.birthday ? new Date(student.birthday).toLocaleDateString() : 'N/A'}</td>
                                    <td className="py-3 px-4 text-sm">{student.is_trial ? 'Yes' : 'No'}</td>
                                    <td className="py-3 px-4">
                                        <button onClick={() => handleOpenEditModal(student)} className="text-blue-600 hover:text-blue-800 text-sm font-semibold">Edit</button>
                                        <button onClick={() => handleDeleteStudent(student.id)} className="text-red-600 hover:text-red-800 text-sm ml-4 font-semibold">Delete</button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={isAdmin ? 8 : 7}> 
                                    <EmptyState message={
                                        categoryFilter === 'all' 
                                            ? "No students found. Use the '+ Add Student' button above or 'Import from CSV' to add some."
                                            : `No ${categoryFilter === 'cram_school' ? 'cram school' : 'private'} students found.`
                                    } />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <Modal title="Add New Student" isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>
                <form onSubmit={handleAddStudent} className="space-y-3">
                    {addError && <p className="text-red-500 text-sm mb-2">{addError}</p>}

                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                            {t('stu_category')}
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                onClick={() => setNewStudentType('private')}
                                className={`py-2 px-3 rounded-lg text-sm font-medium border flex items-center gap-2 transition-colors ${
                                    newStudentType === 'private'
                                        ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-sm'
                                        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50'
                                }`}
                            >
                                <span className="text-lg">🏠</span>
                                <div className="text-left">
                                    <div className="font-semibold leading-tight">{t('stu_private')}</div>
                                    <div className="text-[10px] text-gray-500 dark:text-gray-400">{t('stu_private_desc')}</div>
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setNewStudentType('cram_school')}
                                className={`py-2 px-3 rounded-lg text-sm font-medium border flex items-center gap-2 transition-colors ${
                                    newStudentType === 'cram_school'
                                        ? 'bg-amber-50 border-amber-500 text-amber-800 shadow-sm'
                                        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50'
                                }`}
                            >
                                <span className="text-lg">🏫</span>
                                <div className="text-left">
                                    <div className="font-semibold leading-tight">{t('stu_cram')}</div>
                                    <div className="text-[10px] text-gray-500 dark:text-gray-400">{t('stu_cram_desc')}</div>
                                </div>
                            </button>
                        </div>
                    </div>

                    {newStudentType === 'cram_school' ? (
                        <div className="space-y-3 pt-1">
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-start gap-2">
                                <span className="text-base">ℹ️</span>
                                <div>
                                    <strong>{language === 'zh' ? '補習班學生：' : 'Cram School Student:'}</strong> {language === 'zh' ? '非獨立個別客戶，僅需填寫姓名與生日以供點名。' : 'Not a direct client. Only Name and Birthday are needed for attendance tracking.'}
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">{t('stu_branch_name')}</label>
                                <input
                                    type="text"
                                    list="cram-school-options-add"
                                    value={newCramSchoolName}
                                    onChange={e => setNewCramSchoolName(e.target.value)}
                                    placeholder="e.g. Joy English, 何嘉仁..."
                                    className="w-full border rounded p-2 text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none"
                                />
                                <datalist id="cram-school-options-add">
                                    {cramSchoolOptions.map(name => <option key={name} value={name} />)}
                                </datalist>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    {t('stu_full_name')} <span className="text-red-500">*</span>
                                </label>
                                <input type="text" value={newName} onChange={e => setNewName(e.target.value)} className="w-full border rounded p-2 text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" required />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">{t('stu_birthday')}</label>
                                <input type="date" value={newBirthday} onChange={e => setNewBirthday(e.target.value)} className="w-full border rounded p-2 text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" />
                            </div>
                            <div className="flex items-center gap-2 pt-1">
                                <input type="checkbox" checked={newIsTrial} onChange={e => setNewIsTrial(e.target.checked)} id="new_is_trial_cram" className="rounded text-amber-600 focus:ring-amber-500 h-4 w-4" />
                                <label htmlFor="new_is_trial_cram" className="text-xs text-gray-700 dark:text-gray-300 cursor-pointer">{t('stu_trial_student')}</label>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">{t('stu_notes')}</label>
                                <textarea
                                    value={newParentInfo}
                                    onChange={e => setNewParentInfo(e.target.value)}
                                    placeholder="Class level, student seat number, or internal notes..."
                                    className="w-full border rounded p-2 text-sm"
                                    rows="2"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Full Name <span className="text-red-500">*</span></label>
                                <input type="text" value={newName} onChange={e => setNewName(e.target.value)} className="w-full border rounded p-2 text-sm" required />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Email <span className="text-red-500">*</span></label>
                                <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} className="w-full border rounded p-2 text-sm" required />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Phone (Optional)</label>
                                    <input type="tel" value={newPhone} onChange={e => setNewPhone(e.target.value)} className="w-full border rounded p-2 text-sm" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Birthday (Optional)</label>
                                    <input type="date" value={newBirthday} onChange={e => setNewBirthday(e.target.value)} className="w-full border rounded p-2 text-sm" />
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <input type="checkbox" checked={newIsTrial} onChange={e => setNewIsTrial(e.target.checked)} id="new_is_trial" className="rounded h-4 w-4" />
                                <label htmlFor="new_is_trial" className="text-xs text-gray-700 dark:text-gray-300 cursor-pointer">Is this a trial student?</label>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Parent Contact Info (Optional)</label>
                                <textarea value={newParentInfo} onChange={e => setNewParentInfo(e.target.value)} className="w-full border rounded p-2 text-sm" rows="2" />
                            </div>
                            <hr className="my-3" />
                            <h4 className="font-semibold text-gray-800 dark:text-gray-200 text-sm">💰 Billing & Family Settings</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div>
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Parent Name</label>
                                    <input type="text" value={newParentName} onChange={e => setNewParentName(e.target.value)} placeholder="e.g. Mrs. Chen" className="w-full border rounded p-2 text-sm" />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Parent Phone (Sibling Linking)</label>
                                    <input type="tel" value={newParentPhone} onChange={e => setNewParentPhone(e.target.value)} placeholder="0912-345-678" className="w-full border rounded p-2 text-sm" />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Parent LINE ID (For Unified Notices)</label>
                                <input type="text" value={newParentLineId} onChange={e => setNewParentLineId(e.target.value)} placeholder="e.g. chen_line" className="w-full border rounded p-2 text-sm" />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div>
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Billing Schedule</label>
                                    <select value={newPaymentSchedule} onChange={e => setNewPaymentSchedule(e.target.value)} className="w-full border rounded p-2 text-sm">
                                        <option value="per_class">Per-Class (Weekly / Day of Class)</option>
                                        <option value="monthly">Monthly Bill</option>
                                        <option value="per_4_sessions">Every 4 Sessions (Tutoring Block)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Preferred Payment Method</label>
                                    <select value={newPaymentMethod} onChange={e => setNewPaymentMethod(e.target.value)} className="w-full border rounded p-2 text-sm">
                                        <option value="cash">{t('pay_cash')}</option>
                                        <option value="line_pay">{t('pay_line_pay')}</option>
                                        <option value="bank_transfer">{t('pay_bank')}</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Custom Fee Override NTD (Optional)</label>
                                <input type="number" value={newCustomRate} onChange={e => setNewCustomRate(e.target.value)} placeholder="Leave blank for auto 650/350/300 rate" className="w-full border rounded p-2 text-sm" />
                            </div>
                            <hr className="my-3" />
                            <h4 className="font-semibold text-gray-800 dark:text-gray-200 text-sm">Portal Login</h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Optional. Set credentials for the student/parent portal.</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div>
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Portal Username</label>
                                    <input type="text" value={newUsername} onChange={e => setNewUsername(e.target.value)} className="w-full border rounded p-2 text-sm" />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Set Password</label>
                                    <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full border rounded p-2 text-sm" />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end space-x-2 pt-3 border-t">
                        <button type="button" onClick={() => setIsAddModalOpen(false)} className="bg-gray-200 hover:bg-gray-300 text-gray-800 dark:text-gray-200 px-4 py-2 rounded text-sm font-medium">Cancel</button>
                        <button type="submit" className={`px-4 py-2 rounded text-sm font-medium text-white shadow-sm ${newStudentType === 'cram_school' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                            Add Student
                        </button>
                    </div>
                </form>
            </Modal>

            {editingStudent && (
                <Modal title="Edit Student" isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
                        <form onSubmit={handleUpdateStudent} className="space-y-3">
                            {/* Student Category Selector */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                                    {t('stu_category')}
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setEditingStudent(prev => ({ ...prev, student_type: 'private' }))}
                                        className={`py-2 px-3 rounded-lg text-sm font-medium border flex items-center gap-2 transition-colors ${
                                            (editingStudent.student_type || 'private') === 'private'
                                                ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-sm'
                                                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50'
                                        }`}
                                    >
                                        <span className="text-lg">🏠</span>
                                        <div className="text-left">
                                            <div className="font-semibold leading-tight">{t('stu_private')}</div>
                                            <div className="text-[10px] text-gray-500 dark:text-gray-400">{t('stu_private_desc')}</div>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setEditingStudent(prev => ({ ...prev, student_type: 'cram_school' }))}
                                        className={`py-2 px-3 rounded-lg text-sm font-medium border flex items-center gap-2 transition-colors ${
                                            editingStudent.student_type === 'cram_school'
                                                ? 'bg-amber-50 border-amber-500 text-amber-800 shadow-sm'
                                                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50'
                                        }`}
                                    >
                                        <span className="text-lg">🏫</span>
                                        <div className="text-left">
                                            <div className="font-semibold leading-tight">{t('stu_cram')}</div>
                                            <div className="text-[10px] text-gray-500 dark:text-gray-400">{t('stu_cram_desc')}</div>
                                        </div>
                                    </button>
                                </div>
                            </div>

                            {editingStudent.student_type === 'cram_school' ? (
                                <div className="space-y-3 pt-1">
                                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-start gap-2">
                                        <span className="text-base">ℹ️</span>
                                        <div>
                                            <strong>{language === 'zh' ? '補習班學生：' : 'Cram School Student:'}</strong> {language === 'zh' ? '非獨立個別客戶，僅需填寫姓名與生日以供點名。' : 'Not a direct client. Only Name and Birthday are needed for attendance tracking.'}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                            {t('stu_branch_name')}
                                        </label>
                                        <input
                                            type="text"
                                            name="cram_school_name"
                                            list="cram-school-options-edit"
                                            value={editingStudent.cram_school_name || ''}
                                            onChange={handleEditFormChange}
                                            placeholder="e.g. Joy English, 何嘉仁..."
                                            className="w-full border rounded p-2 text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none"
                                        />
                                        <datalist id="cram-school-options-edit">
                                            {cramSchoolOptions.map(name => <option key={name} value={name} />)}
                                        </datalist>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                            {t('stu_full_name')} <span className="text-red-500">*</span>
                                        </label>
                                        <input 
                                            type="text" 
                                            name="name" 
                                            value={editingStudent.name} 
                                            onChange={handleEditFormChange} 
                                            className="w-full border rounded p-2 text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" 
                                            required 
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                            {t('stu_birthday')}
                                        </label>
                                        <input 
                                            type="date" 
                                            name="birthday" 
                                            value={editingStudent.birthday} 
                                            onChange={handleEditFormChange} 
                                            className="w-full border rounded p-2 text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" 
                                        />
                                    </div>

                                    <div className="flex items-center gap-2 pt-1">
                                        <input 
                                            type="checkbox" 
                                            name="is_trial" 
                                            checked={editingStudent.is_trial} 
                                            onChange={handleEditFormChange} 
                                            id="is_trial_cram_edit" 
                                            className="rounded text-amber-600 focus:ring-amber-500 h-4 w-4" 
                                        />
                                        <label htmlFor="is_trial_cram_edit" className="text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                                            {t('stu_trial_student')}
                                        </label>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                            {t('stu_notes')}
                                        </label>
                                        <textarea 
                                            name="parent_info"
                                            value={editingStudent.parent_info || ''} 
                                            onChange={handleEditFormChange} 
                                            placeholder="Class level, student seat number, or internal notes..."
                                            className="w-full border rounded p-2 text-sm" 
                                            rows="2"
                                        />
                                    </div>
                                </div>
                            ) : (
                                /* Full Private Student Edit Form */
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Full Name</label>
                                        <input type="text" name="name" value={editingStudent.name} onChange={handleEditFormChange} className="w-full border rounded p-2 text-sm" required />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Email</label>
                                        <input type="email" name="email" value={editingStudent.email || ''} onChange={handleEditFormChange} className="w-full border rounded p-2 text-sm" required />
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Birthday (Optional)</label>
                                            <input type="date" name="birthday" value={editingStudent.birthday} onChange={handleEditFormChange} className="w-full border rounded p-2 text-sm" />
                                        </div>
                                        <div className="flex items-center pt-5">
                                            <input type="checkbox" name="is_trial" checked={editingStudent.is_trial} onChange={handleEditFormChange} id="is_trial_edit" className="rounded mr-2 h-4 w-4" />
                                            <label htmlFor="is_trial_edit" className="text-xs text-gray-700 dark:text-gray-300 cursor-pointer">Is this a trial student?</label>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Parent Contact Info (Optional)</label>
                                        <textarea 
                                            name="parent_info"
                                            value={editingStudent.parent_info || ''} 
                                            onChange={handleEditFormChange} 
                                            className="w-full border rounded p-2 text-sm"
                                            rows="2"
                                        ></textarea>
                                    </div>
                                    <hr className="my-3" />
                                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 text-sm">💰 Billing & Family Settings</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <div>
                                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Parent Name</label>
                                            <input type="text" name="parent_name" value={editingStudent.parent_name || ''} onChange={handleEditFormChange} placeholder="e.g. Mrs. Chen" className="w-full border rounded p-2 text-sm" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Parent Phone (Sibling Linking)</label>
                                            <input type="tel" name="parent_phone" value={editingStudent.parent_phone || ''} onChange={handleEditFormChange} placeholder="0912-345-678" className="w-full border rounded p-2 text-sm" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Parent LINE ID (For Unified Notices)</label>
                                        <input type="text" name="parent_line_id" value={editingStudent.parent_line_id || ''} onChange={handleEditFormChange} placeholder="e.g. chen_line" className="w-full border rounded p-2 text-sm" />
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <div>
                                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Billing Schedule</label>
                                            <select name="payment_schedule" value={editingStudent.payment_schedule || 'per_class'} onChange={handleEditFormChange} className="w-full border rounded p-2 text-sm">
                                                <option value="per_class">Per-Class (Weekly / Day of Class)</option>
                                                <option value="monthly">Monthly Bill</option>
                                                <option value="per_4_sessions">Every 4 Sessions (Tutoring Block)</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Preferred Payment Method</label>
                                            <select name="payment_method" value={editingStudent.payment_method || 'cash'} onChange={handleEditFormChange} className="w-full border rounded p-2 text-sm">
                                                <option value="cash">{t('pay_cash')}</option>
                                                <option value="line_pay">{t('pay_line_pay')}</option>
                                                <option value="bank_transfer">{t('pay_bank')}</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Custom Fee Override NTD (Optional)</label>
                                        <input type="number" name="custom_rate" value={editingStudent.custom_rate || ''} onChange={handleEditFormChange} placeholder="Leave blank for auto 650/350/300 rate" className="w-full border rounded p-2 text-sm" />
                                    </div>
                                    <hr className="my-3" />
                                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 text-sm">Portal Login</h4>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Optional. Set credentials for the student/parent portal.</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <div>
                                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Portal Username</label>
                                            <input 
                                                type="text" 
                                                name="username" 
                                                value={editingStudent.username || ''}
                                                onChange={handleEditFormChange}
                                                className="w-full border rounded p-2 text-sm" 
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Set/Reset Password</label>
                                            <input 
                                                type="password" 
                                                name="password" 
                                                value={editingStudent.password}
                                                onChange={handleEditFormChange}
                                                className="w-full border rounded p-2 text-sm" 
                                                placeholder="Leave blank to keep unchanged"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end space-x-2 pt-3 border-t">
                                <button type="button" onClick={() => setIsEditModalOpen(false)} className="bg-gray-200 hover:bg-gray-300 text-gray-800 dark:text-gray-200 px-4 py-2 rounded text-sm font-medium">
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className={`px-4 py-2 rounded text-sm font-medium text-white shadow-sm ${
                                        editingStudent.student_type === 'cram_school'
                                            ? 'bg-amber-600 hover:bg-amber-700'
                                            : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                                >
                                    Save Changes
                                </button>
                            </div>
                        </form>
                </Modal>
            )}
            {historyStudent && (
                <Modal title={`Attendance History for ${historyStudent.name}`} isOpen={historyModalOpen} onClose={() => setHistoryModalOpen(false)}>
                    {historyLoading ? <Spinner /> : (
                        <div className="max-h-96 overflow-y-auto">
                            {historyData.length > 0 ? (
                                <ul className="space-y-3">
                                    {historyData.map((record, index) => (
                                        <li key={index} className="p-2 border-b">
                                            <p className="font-semibold">{record.class_name} on {new Date(record.attendance_timestamp).toLocaleDateString()}</p>
                                            <p>Status: <span className="font-medium">{record.attendance_status}</span></p>
                                            {record.notes && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Note: {record.notes}</p>}
                                        </li>
                                    ))}
                                </ul>
                            ) : <EmptyState message="No attendance history found for this student." />}
                        </div>
                    )}
                </Modal>
            )}

            {/* Multi-Pick Class Assignment Modal */}
            {isAssignModalOpen && (
                <Modal 
                    title={`Assign ${selectedStudentIds.length} Student${selectedStudentIds.length > 1 ? 's' : ''} to Class(es)`} 
                    isOpen={isAssignModalOpen} 
                    onClose={() => setIsAssignModalOpen(false)}
                >
                    <div className="space-y-4">
                        {/* Selected Students Chips */}
                        <div className="bg-gray-50 dark:bg-gray-800/60 p-3 rounded-xl border border-gray-200 dark:border-gray-700">
                            <p className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                                Selected Students ({selectedStudentIds.length}):
                            </p>
                            <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                                {students
                                    .filter(s => selectedStudentIds.includes(s.id))
                                    .map(s => (
                                        <span 
                                            key={s.id} 
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                        >
                                            {s.student_type === 'cram_school' ? '🏫' : '🏠'} {s.name}
                                        </span>
                                    ))
                                }
                            </div>
                        </div>

                        {/* Search and Category Filters */}
                        <div className="space-y-2">
                            <div className="relative">
                                <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 dark:text-gray-500 text-xs">🔍</span>
                                <input
                                    type="text"
                                    value={classSearchTerm}
                                    onChange={e => setClassSearchTerm(e.target.value)}
                                    placeholder="Search classes by name, teacher, cram school, day..."
                                    className="w-full pl-8 pr-3 py-2 border border-gray-200 dark:border-gray-700 rounded-xl text-xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                />
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                                <div className="flex items-center gap-1.5">
                                    <button
                                        type="button"
                                        onClick={() => setClassFilter('all')}
                                        className={`px-2.5 py-1 rounded-lg font-bold text-[11px] transition-colors cursor-pointer ${
                                            classFilter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200'
                                        }`}
                                    >
                                        All ({availableClasses.length})
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setClassFilter('private')}
                                        className={`px-2.5 py-1 rounded-lg font-bold text-[11px] transition-colors cursor-pointer ${
                                            classFilter === 'private' ? 'bg-emerald-700 text-white' : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                                        }`}
                                    >
                                        🏠 Private
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setClassFilter('cram_school')}
                                        className={`px-2.5 py-1 rounded-lg font-bold text-[11px] transition-colors cursor-pointer ${
                                            classFilter === 'cram_school' ? 'bg-purple-700 text-white' : 'bg-purple-50 text-purple-800 hover:bg-purple-100'
                                        }`}
                                    >
                                        🏫 Cram School
                                    </button>
                                </div>

                                {filteredAvailableClasses.length > 0 && (
                                    <label className="flex items-center gap-1.5 cursor-pointer font-medium text-gray-600 dark:text-gray-400 select-none">
                                        <input
                                            type="checkbox"
                                            checked={filteredAvailableClasses.length > 0 && filteredAvailableClasses.every(c => selectedClassIds.includes(c.id))}
                                            onChange={handleSelectAllClasses}
                                            className="w-3.5 h-3.5 rounded text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                        />
                                        <span>Select All ({filteredAvailableClasses.length})</span>
                                    </label>
                                )}
                            </div>
                        </div>

                        {/* Classes List */}
                        {classesLoading ? (
                            <div className="py-10 text-center"><Spinner /></div>
                        ) : (
                            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                                {filteredAvailableClasses.length > 0 ? (
                                    filteredAvailableClasses.map(cls => {
                                        const isSelected = selectedClassIds.includes(cls.id);
                                        const isCram = cls.class_type === 'cram_school';
                                        return (
                                            <div
                                                key={cls.id}
                                                onClick={() => handleToggleClass(cls.id)}
                                                className={`flex items-center justify-between p-2.5 rounded-xl border transition-all cursor-pointer select-none ${
                                                    isSelected 
                                                        ? 'bg-emerald-50/80 border-emerald-300 shadow-xs' 
                                                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50'
                                                }`}
                                            >
                                                <div className="flex items-center gap-2.5 min-w-0">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => {}}
                                                        className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                                    />
                                                    <div className="truncate">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-bold text-xs text-gray-900 dark:text-gray-100 truncate">{cls.name}</span>
                                                            <span className={`px-1.5 py-0.2 rounded font-semibold text-[10px] ${
                                                                isCram ? 'bg-purple-100 text-purple-800' : 'bg-emerald-100 text-emerald-800'
                                                            }`}>
                                                                {isCram ? `🏫 ${cls.cram_school_name || 'Cram'}` : '🏠 Private'}
                                                            </span>
                                                        </div>
                                                        <div className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-2 mt-0.5">
                                                            <span>📅 {cls.day} {cls.start_time?.slice(0, 5)}-{cls.end_time?.slice(0, 5)}</span>
                                                            {cls.teacher_name && <span>• 👨‍🏫 {cls.teacher_name}</span>}
                                                            <span>• 👥 {cls.student_count || 0} enrolled</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <span className={`text-xs font-bold px-2 py-1 rounded-lg ${
                                                    isSelected ? 'bg-emerald-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                                }`}>
                                                    {isSelected ? '✓ Picked' : '+ Pick'}
                                                </span>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="py-8 text-center text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800/60 rounded-xl border border-dashed text-xs">
                                        No classes match your filter or search.
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Modal Footer */}
                        <div className="flex items-center justify-between pt-3 border-t">
                            <button
                                type="button"
                                onClick={() => setIsAssignModalOpen(false)}
                                className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-xl text-xs font-semibold cursor-pointer"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={selectedClassIds.length === 0}
                                onClick={handleConfirmAssignClasses}
                                className={`px-4 py-2 rounded-xl text-xs font-bold text-white shadow-xs transition-all cursor-pointer ${
                                    selectedClassIds.length > 0
                                        ? 'bg-emerald-600 hover:bg-emerald-700'
                                        : 'bg-gray-300 cursor-not-allowed'
                                }`}
                            >
                                Enroll {selectedStudentIds.length} Student{selectedStudentIds.length > 1 ? 's' : ''} in {selectedClassIds.length} Class{selectedClassIds.length > 1 ? 'es' : ''} ➔
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Floating Mobile Action Bar when items selected */}
            {isAdmin && selectedStudentIds.length > 0 && (
                <div className="fixed bottom-4 left-4 right-4 md:hidden z-40 bg-gray-900 text-white p-3 rounded-2xl shadow-2xl flex items-center justify-between gap-2 border border-gray-700">
                    <span className="text-xs font-bold pl-1">
                        {selectedStudentIds.length} student{selectedStudentIds.length > 1 ? 's' : ''} picked
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleOpenAssignModal}
                            className="bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1 cursor-pointer shadow-xs"
                        >
                            <span>🏫</span> Assign
                        </button>
                        <button
                            onClick={handleBulkDelete}
                            className="bg-red-600 hover:bg-red-500 text-white px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1 cursor-pointer shadow-xs"
                        >
                            <span>🗑️</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}